
import type { IResponse } from "../@types/ApiResponse";
import { emptySplitApi } from "./emptySliceApi";
import endpoints from "./endpoints";

export const screenFrameConfigApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    GetScreenFrameConfigs: builder.query<IResponse<ScreenFrameConfig.ScreenFrameConfigList[]>, ScreenFrameConfig.ScreenFrameFilter>({
      query: (filter) => {
        return {
          url: endpoints.ScreenFrameConfigs.base,
          method: "GET",
          params: filter,
        };
      },
      providesTags: ["ScreenFrameConfig"],
    }),
    GetScreenFrameConfigById: builder.query<IResponse<ScreenFrameConfig.ScreenFrameDetail>, string | number>({
      query: (id) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.base}/${Number(id)}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, id) => [{ type: "ScreenFrameConfig", id }],
    }),
    CreateScreenFrameConfig: builder.mutation<
      IResponse<ScreenFrameConfig.ScreenFrameDetail>,
      ScreenFrameConfig.ScreenFrameDto
    >({
      query: (formData) => {
        return {
          url: endpoints.ScreenFrameConfigs.base,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["ScreenFrameConfig"],
    }),
    UpdateScreenFrameConfig: builder.mutation<
      IResponse<ScreenFrameConfig.ScreenFrameDetail>,
      { id: string | number; data: ScreenFrameConfig.UpdateScreenFrameDto }
    >({
      query: ({ id, data }) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.base}/${id}`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        "ScreenFrameConfig",
        { type: "ScreenFrameConfig", id }
      ],
    }),
    DeleteScreenFrameConfig: builder.mutation<IResponse<null>, string | number>({
      query: (id) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.base}/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_result, _error, id) => [
        "ScreenFrameConfig",
        { type: "ScreenFrameConfig", id }
      ],
    }),
    GetScreenFrameConfigsByScreenId: builder.query<IResponse<ScreenFrameConfig.ScreenFrameConfigList[]>, string | number>({
      query: (screenId) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.base}/screen/${screenId}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, screenId) => [
        "ScreenFrameConfig",
        { type: "ScreenFrameConfig", id: `screen-${screenId}` }
      ],
    }),
    GetScreenFrameConfigsByFrameId: builder.query<IResponse<ScreenFrameConfig.ScreenFrameConfigList[]>, string | number>({
      query: (frameId) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.base}/frame/${frameId}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, frameId) => [
        "ScreenFrameConfig",
        { type: "ScreenFrameConfig", id: `frame-${frameId}` }
      ],
    }),
    UpdateScreenFrameConfigContents: builder.mutation<
      IResponse<null>,
      { configId: string | number; contents: ScreenFrameConfig.ComponentContentDto[] }
    >({
      query: ({ configId, contents }) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.base}/${configId}/contents`,
          method: "PUT",
          body: { contents },
        };
      },
      invalidatesTags: (_result, _error, { configId }) => [
        "ScreenFrameConfig",
        { type: "ScreenFrameConfig", id: configId }
      ],
    }),
    GetScreenFrameConfigByScreenId: builder.query<
      IResponse<ScreenFrameConfig.ScreenFrameConfigList[]>,
      { screenId: number }
    >({
      query: ({ screenId }) => {
        return {
          url: `${endpoints.ScreenFrameConfigs.byScreenId(screenId)}`,
          method: "GET",
        };
      },
      providesTags: ["ScreenFrameConfig"],
    }),
    UpdateScreenFrameConfigOrder: builder.mutation<
      IResponse<any>,
      ScreenFrameConfig.FrameConfigOrder
    >({
      query: (orderList) => {
        return {
          url: endpoints.ScreenFrameConfigs.frameOrder,
          method: "POST",
          body: orderList,
        };
      },
      invalidatesTags: ["ScreenFrameConfig"],
    }),
    UpdateScreenFrameConfigStatus: builder.mutation<
      IResponse<null>,
      ScreenFrameConfig.StatusUpdate
    >({
      query: (statusData) => {
        return {
          url: endpoints.ScreenFrameConfigs.status,
          method: "POST",
          body: statusData,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        "ScreenFrameConfig",
        { type: "ScreenFrameConfig", id }
      ],
    }),
  }),
});

export const {
  useGetScreenFrameConfigsQuery,
  useGetScreenFrameConfigByIdQuery,
  useCreateScreenFrameConfigMutation,
  useUpdateScreenFrameConfigMutation,
  useDeleteScreenFrameConfigMutation,
  useGetScreenFrameConfigsByScreenIdQuery,
  useGetScreenFrameConfigsByFrameIdQuery,
  useUpdateScreenFrameConfigContentsMutation,
  useGetScreenFrameConfigByScreenIdQuery,
  useUpdateScreenFrameConfigOrderMutation,
  useUpdateScreenFrameConfigStatusMutation
} = screenFrameConfigApi;